import { Button } from '@unified-lk/ui'
import { formatDateTime } from '../../lib/format'
import type { StaffDashboardData } from '../../types/plans'
import { summarizeTodayAppointments } from '../../domain/appointments/appointmentStatusPresentation'
import { staffAppointmentsHint, staffDashboardGreeting } from '../../domain/tenant/staffTenantCopy'
import { StaffMetricCard, StaffStatusBadge } from '../../shared/staff-ui'

function formatAppointmentTime(value: string) {
  const parts = formatDateTime(value).split(', ')
  return parts[1] ?? formatDateTime(value)
}

function CalendarIcon() {
  return (
    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-600">
      <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    </div>
  )
}

function PlansIcon() {
  return (
    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-emerald-500">
      <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    </div>
  )
}

function ChatIcon() {
  return (
    <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-500">
      <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
      </svg>
    </div>
  )
}

export function StaffDashboardSection({
  data,
  loading,
  error,
  tenant,
  onRetry,
  onNavigate,
}: {
  data: StaffDashboardData | null
  loading: boolean
  error: string | null
  tenant: string
  onRetry: () => void
  onNavigate?: (section: string) => void
}) {
  if (loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm">
        Загружаем дашборд...
      </div>
    )
  }

  if (!data) {
    return (
      <div className="grid gap-3 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-sm text-slate-500">{error ?? 'Данные dashboard пока недоступны.'}</p>
        <Button variant="secondary" type="button" onClick={onRetry}>Повторить</Button>
      </div>
    )
  }

  const appointmentStats = summarizeTodayAppointments(data.today_appointments)
  const operationalBlocks = data.focus_blocks.filter((block) => !/выруч|revenue|доход|finance/i.test(`${block.title} ${block.description}`))

  return (
    <div className="mx-auto max-w-7xl">
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 sm:text-3xl">
            Добрый день, {data.staff.display_name.split(' ')[0]}
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            {new Date().toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            {' · '}
            {staffDashboardGreeting(tenant)}
          </p>
        </div>
        <button
          type="button"
          onClick={() => onNavigate?.('schedule')}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700"
        >
          <span className="text-lg leading-none">+</span>
          Записи сегодня
        </button>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StaffMetricCard
          label="Записей сегодня"
          value={data.summary.appointments_today}
          hint={`${appointmentStats.finished} окончено · ${appointmentStats.confirmed} подтверждено · ${appointmentStats.upcoming} предстоит`}
          accent="blue"
          icon={<CalendarIcon />}
        />
        <StaffMetricCard
          label="Подтверждено"
          value={appointmentStats.confirmed}
          hint={staffAppointmentsHint(tenant)}
          accent="green"
        />
        <StaffMetricCard
          label="Планы в работе"
          value={data.summary.plans_in_work ?? '—'}
          hint="Активные планы лечения в МИС"
          accent="purple"
          icon={<PlansIcon />}
        />
        <StaffMetricCard
          label="Непрочитанные треды"
          value={data.summary.unread_threads}
          hint="Ожидают ответа команды"
          accent="orange"
          icon={<ChatIcon />}
        />
      </div>

      <div className="mb-6 grid gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:grid-cols-4">
        {[
          { label: 'Окончено', value: appointmentStats.finished, tone: 'text-emerald-700' },
          { label: 'Подтверждено', value: appointmentStats.confirmed, tone: 'text-blue-700' },
          { label: 'Предстоит', value: appointmentStats.upcoming, tone: 'text-slate-700' },
          { label: 'Отменено / неявка', value: appointmentStats.cancelled, tone: 'text-rose-700' },
        ].map((item) => (
          <div key={item.label} className="rounded-lg bg-slate-50 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{item.label}</p>
            <p className={`mt-1 text-2xl font-bold ${item.tone}`}>{item.value}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold text-slate-900">Ближайшие записи</h2>
            <button
              type="button"
              onClick={() => onNavigate?.('schedule')}
              className="text-sm font-medium text-blue-600 hover:text-blue-700"
            >
              Расписание →
            </button>
          </div>

          {data.today_appointments.length > 0 ? (
            <div className="divide-y divide-slate-100">
              {data.today_appointments.map((appointment) => (
                <div key={appointment.id} className="flex items-center justify-between gap-4 py-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                      <span className="text-sm font-semibold text-slate-900">{formatAppointmentTime(appointment.date)}</span>
                      <span className="truncate text-sm text-slate-800">{appointment.patient_name}</span>
                    </div>
                    <p className="mt-1 truncate text-xs text-slate-500">
                      {appointment.service_name}
                      {appointment.branch_name ? ` · ${appointment.branch_name}` : ''}
                      {appointment.room_name ? ` · ${appointment.room_name}` : ''}
                    </p>
                  </div>
                  <StaffStatusBadge status={appointment.status} />
                </div>
              ))}
            </div>
          ) : (
            <div className="py-10 text-center text-sm text-slate-500">Нет приёмов на сегодня</div>
          )}
        </section>

        <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-slate-900">Операционный фокус</h2>
          <div className="space-y-3">
            {(operationalBlocks.length ? operationalBlocks : data.focus_blocks).slice(0, 6).map((block) => (
              <div key={block.title} className="rounded-lg border border-slate-100 bg-slate-50 px-4 py-3">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{block.title}</p>
                    <p className="mt-1 text-xs text-slate-500">{block.description}</p>
                  </div>
                  <span className="shrink-0 text-sm font-bold text-blue-600">{block.value}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  )
}
